// src/hooks/useLinkedinUrlImport.js
// Sprint 1 E1.4b — Importar contato via URL LinkedIn (Apollo enrich by linkedin_url).
//
// Edge Function: enrich-companies-via-apollo (modo create_from_linkedin).
//   body: { mode_create_from_linkedin: true, linkedin_url, decided_by? }
// Resposta esperada:
//   {
//     ok: true,
//     already_exists: bool,
//     contact_id, contact_name,
//     empresa_id, empresa_name,
//     message
//   }

import { useCallback, useState } from 'react';
import { supabase } from '../supabaseClient';
import { useUserRole } from './useUserRole';

const LINKEDIN_RE = /^https?:\/\/(www\.)?linkedin\.com\/in\/[\w\-_%.]+\/?(\?.*)?$/i;

export function useLinkedinUrlImport() {
  const [loading, setLoading] = useState(false);
  const { email } = useUserRole();

  const importFromUrl = useCallback(async (linkedinUrl) => {
    const trimmed = (linkedinUrl || '').trim();
    if (!LINKEDIN_RE.test(trimmed)) {
      return { ok: false, error: 'URL LinkedIn inválida. Formato: https://linkedin.com/in/usuario' };
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        'enrich-companies-via-apollo',
        {
          body: {
            mode_create_from_linkedin: true,
            linkedin_url: trimmed,
            decided_by: email || undefined,
          },
        },
      );

      if (error) {
        // supabase-js entrega a resposta da Edge Function (não-2xx) em error.context
        // (um objeto Response). Sem ler isso, sobra só a mensagem genérica
        // "Edge Function returned a non-2xx status code" → mascara o 422/404 real.
        let payload = null;
        const status = error?.context?.status;
        try {
          if (error?.context && typeof error.context.json === 'function') {
            payload = await error.context.json();
          }
        } catch {
          // corpo não-JSON → cai no fallback de error.message abaixo
        }
        return {
          ok: false,
          status,
          error_code: payload?.error, // ex.: 'invalid_linkedin_url'
          error: payload?.message || payload?.error || error.message || 'Falha ao importar contato.',
        };
      }
      if (data?.error) {
        // Edge Function respondeu 200 com { ok:false, error } (erro de negócio).
        return { ok: false, error_code: data.error, error: data.message || data.error };
      }

      return {
        ok: data?.ok !== false,
        already_exists: !!data?.already_exists,
        contact_id: data?.contact_id,
        contact_name: data?.contact_name,
        empresa_id: data?.empresa_id,
        empresa_name: data?.empresa_name,
        message: data?.message,
      };
    } catch (err) {
      return { ok: false, error: err?.message || String(err) };
    } finally {
      setLoading(false);
    }
  }, [email]);

  return { importFromUrl, loading };
}
